import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Box,
  Typography,
  Divider, Chip, Alert
} from "@mui/material";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ShiftTransferDialog = ({
  dataSource = [],
  isOpen,
  isEditEnabled,
  handleDropdownChange,
  handleConfirmation,
  handleClose,
  translate,
}) => {
  const [selectedUser, setSelectedUser] = useState(null);
    // Ordiniamo prima per disponibilità (true in cima), poi per nome
  const sortedDataSource = [...(dataSource || [])].sort((a, b) => {
    if (a.isAvailable && !b.isAvailable) return -1;
    if (!a.isAvailable && b.isAvailable) return 1;
    return a.nameSurname.localeCompare(b.nameSurname, 'it', { sensitivity: 'base' });
  });
  const handleChange = (_, newValue) => {
    setSelectedUser(newValue);
    // Mantiene la stessa firma dell'evento originale Syncfusion
    if (newValue) {
      handleDropdownChange({ itemData: newValue });
    }
  };

  const handleConfirm = () => {
    handleConfirmation();
    setSelectedUser(null);
  };

  const handleCancel = () => {
    setSelectedUser(null);
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCancel}
      fullWidth
      maxWidth="sm"
    >
      {/* HEADER */}
      <DialogTitle sx={{
        bgcolor: 'primary.main',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        py: 1.5,
      }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
          {translate("peopleAvailableForShiftRequest")}
        </Typography>
      </DialogTitle>

      {/* CONTENUTO */}
      <DialogContent sx={{ pt: '20px !important' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Seleziona la persona a cui vuoi cedere il turno tra quelle disponibili.
        </Typography>

        <Autocomplete
          options={sortedDataSource}
          getOptionLabel={(option) => option.nameSurname || ""}
          isOptionEqualToValue={(option, value) => option.anaUserId === value.anaUserId}
          value={selectedUser}
          onChange={handleChange}
          readOnly={isEditEnabled}
          groupBy={(option) =>
            option.isAvailable ? "🌟 Disponibili per questo turno" : "👥 Altri colleghi con lo stesso ruolo"
          }
          filterOptions={(options, { inputValue }) => {
            if (!inputValue) {
              return options.slice(0, 30); 
            }
            return options.filter((option) =>
              option.nameSurname.toLowerCase().includes(inputValue.toLowerCase())
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Seleziona volontario"
              placeholder="Cerca per nome..."
              fullWidth
            />
          )}
          renderOption={(props, option) => (
            <Box 
              component="li" 
              {...props} 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                opacity: option.isAvailable ? 1 : 0.6, // Rende grigi i non disponibili
                fontStyle: option.isAvailable ? 'normal' : 'italic'
              }}
            >
              {/* Icona verde se disponibile, altrimenti nessuna icona */}
              {option.isAvailable ? (
                <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />
              ) : (
                <Box sx={{ width: 20 }} /> // Spaziatore per allineare i nomi
              )}
              <Typography variant="body2" fontWeight={option.isAvailable ? "bold" : "normal"}>
                {option.nameSurname}
              </Typography>
            </Box>
          )}
          noOptionsText="Nessun volontario trovato"
        />
         {/* HELPER TEXT PER AVVISARE DEL LIMITE */}
        {dataSource.length > 30 && !selectedUser && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', fontStyle: 'italic' }}>
            Mostrati i primi 30 colleghi. Digita un nome per cercare nell'elenco completo.
          </Typography>
        )}
        {/* 4. AVVISO SE NON DISPONIBILE */}
        {selectedUser && !selectedUser.isAvailable && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Stai cedendo il turno a un collega che non ha espresso disponibilità per questo turno.
          </Alert>
        )}
        {/* ANTEPRIMA SELEZIONE O AVVISO */}
        {selectedUser && (
          <Box sx={{
            mt: 2,
            p: 1.5,
            bgcolor: selectedUser.isAvailable ? '#e3f2fd' : '#fff8e1', // Azzurro se disp., Giallo se non disp.
            borderRadius: 1,
            border: '1px solid',
            borderColor: selectedUser.isAvailable ? 'primary.light' : 'warning.light',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <CompareArrowsIcon color={selectedUser.isAvailable ? "primary" : "warning"} fontSize="small" />
            <Typography variant="body2" color={selectedUser.isAvailable ? "primary.main" : "warning.dark"} fontWeight="bold">
              Cederai il turno a: {selectedUser.nameSurname} {!selectedUser.isAvailable && "(Non disponibile)"}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* AZIONI */}
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={handleCancel}
          variant="outlined"
          color="inherit"
          fullWidth
        >
          {translate("cancelBtn")}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          fullWidth
          disabled={!selectedUser}
        >
          {translate("okBtn")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShiftTransferDialog;